import { mapIOErrorToLocalizedObject } from '@eversports/core-io/helpers'
import { isIOError } from '@eversports/io-error'
import ClientLogger from '@eversports/react-components/utilities/client-logger'
import React, { useEffect, useState } from 'react'
import { useLocation } from 'react-router-dom'

import useCart from '../../../data/useCart'
import useCheckoutContext from '../../../data/useCheckoutContext'
import useMutationWithErrorHandling from '../../../data/useMutationWithErrorHandling'
import getPhoenixUrlParams from '../../../lib/get-phoenix-url-params'
import { AddOnsInitialValues } from '../../../typings/AdditionalInformation'
import SkeletonAddons from '../Skeletons/SkeletonAddons'

import {
  AddOnsCartDocument,
  AddOnsCartQuery,
  AddOnsSetAddOnsForBookableItemCartItemDocument,
  AddOnsSetAddOnsForBookableItemCartItemMutation,
  AddOnsSetAddOnsForBookableItemCartItemMutationVariables,
} from '../../../graphql'
import AddOnsComponent from './AddOnsComponent'

import getAddOnsValues from './get-add-ons-values'
import getInitialValues from './get-initial-values'
import getValidation from './get-validation'

const AddOns: React.FC<React.PropsWithChildren<unknown>> = () => {
  const location = useLocation()
  const context = useCheckoutContext()
  const { cartId } = getPhoenixUrlParams(location.search)

  const [initialValues, setInitialValues] = useState<AddOnsInitialValues>()

  const { cartData, isCartLoading } = useCart<AddOnsCartQuery>(AddOnsCartDocument)

  const [setAddOnsForBookableItem] = useMutationWithErrorHandling<
    AddOnsSetAddOnsForBookableItemCartItemMutation,
    AddOnsSetAddOnsForBookableItemCartItemMutationVariables
  >(AddOnsSetAddOnsForBookableItemCartItemDocument, {})

  useEffect(() => {
    if (!cartData) return
    if (cartData.cart.items[0].__typename !== 'BookableItemCartItem' || initialValues) return

    const hasMandatoryAddOn = cartData.cart.items[0].addOns.some((addon) => addon.mandatory)
    const addOnsInitialValues = getInitialValues(cartData.cart.items[0].addOnsInput, cartData.cart.items[0].addOns)

    try {
      const validateInitialValues = getValidation(cartData.cart.items[0].addOns)
      setInitialValues(addOnsInitialValues)
      validateInitialValues(addOnsInitialValues)
    } catch (e: any) {
      context.setIsAddOnsValid(false)
      return
    }

    // if either the cart is already valid, or we have no mandatory addons at all, set addons validity to true
    if ((cartData && cartData.cart.valid) || !hasMandatoryAddOn) {
      context.setIsAddOnsValid(true)
    }
  }, [cartData])

  // Render nothing if:
  // - there's no addOns to fill
  // - or we are on a product cart item
  const cartItem = cartData && cartData.cart.items[0]
  if (cartItem && (cartItem.__typename === 'ProductCartItem' || cartItem.addOns.length === 0)) {
    context.setIsAddOnsValid(true)
    return null
  }

  if (!cartItem || isCartLoading || !initialValues) {
    return <SkeletonAddons />
  }

  const validate = getValidation(cartItem.addOns)

  const handleChange = (values: AddOnsInitialValues) => {
    // Set addons on the cart, in order to update the price table and only then
    // do the respective validation to check if its okay to set it's validity
    // to true in the checkout context
    const addOnValues = getAddOnsValues(cartItem.addOns, values)
    const itemId = cartItem && cartItem.id

    // eslint-disable-next-line  @typescript-eslint/no-floating-promises
    setAddOnsForBookableItem({ variables: { cartId: cartId as string, itemId, addOns: addOnValues } })

    try {
      validate(values)
    } catch (e: any) {
      context.setIsAddOnsValid(false)
      if (!isIOError(e)) {
        return ClientLogger.error('Addons validation failed', e)
      }
      return mapIOErrorToLocalizedObject(e)
    }

    context.setIsAddOnsValid(true)
  }

  return <AddOnsComponent initialValues={initialValues} addOns={cartItem.addOns} handleChange={handleChange} />
}

export default AddOns
